import React, { useMemo, useState } from 'react';
import { Order, OrderStatus, Location } from '../types';
import OrderCard from './OrderCard.tsx';
import { Filter, Calendar, Search } from 'lucide-react';
import { LOCATIONS, STATUSES } from '../constants';
import { formatDate } from '../utils';

interface DashboardProps {
  orders: Order[];
  onUpdateStatus: (id: string, status: OrderStatus) => void;
  onEditOrder: (order: Order) => void;
  onItemToggle: (orderId: string, itemId: string) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ orders, onUpdateStatus, onEditOrder, onItemToggle }) => {
  const [filterLocation, setFilterLocation] = useState<Location | 'All'>('All');
  const [filterStatus, setFilterStatus] = useState<OrderStatus | 'All' | 'Active'>('Active');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Filter and Group Logic
  const groupedOrders = useMemo(() => {
    // 1. Filter orders based on status, location, and search term
    const activeOrders = orders.filter(o => {
        let statusMatch = true;

        if (filterStatus === 'Active') {
            // Default view: Hide Picked Up and Cancelled
            statusMatch = o.status !== 'Picked Up' && o.status !== 'Cancelled';
        } else if (filterStatus === 'All') {
            statusMatch = true;
        } else {
            // Specific status filter
            statusMatch = o.status === filterStatus;
        }

        const locMatch = filterLocation === 'All' || o.location === filterLocation;

        // Search term filter
        let searchMatch = true;
        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase().trim();
            searchMatch = 
                o.firstName.toLowerCase().includes(term) || 
                o.lastName.toLowerCase().includes(term) || 
                o.phone.includes(term);
        }

        return statusMatch && locMatch && searchMatch;
    });

    // 2. Sort by Date
    activeOrders.sort((a, b) => new Date(a.pickupDate).getTime() - new Date(b.pickupDate).getTime());

    // 3. Group by Date
    const groups: Record<string, Order[]> = {};
    activeOrders.forEach(order => {
        const dateKey = order.pickupDate;
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(order);
    });

    return groups;
  }, [orders, filterLocation, filterStatus, searchTerm]);

  const dates = Object.keys(groupedOrders);

  const formatDateHeader = (dateStr: string) => {
    const date = new Date(dateStr);
    // Create a base config object that enforces UTC
    const timeZoneConfig = { timeZone: 'UTC' };
    const dayName = date.toLocaleDateString('en-US', { ...timeZoneConfig, weekday: 'long' });
    // Format as "Dec 10, 2025" instead of "12/10/2025"
    const fullDate = date.toLocaleDateString('en-US', {
      ...timeZoneConfig,
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return { dayName, fullDate };
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
           <h2 className="text-2xl font-bold text-gray-800">Order Board</h2>
           <p className="text-gray-500 text-sm">
             {searchTerm ? 
               `Searching for "${searchTerm}" in customer names and phone numbers.` : 
               (filterStatus === 'Active' ? 'Active orders grouped by pickup date.' : `Orders filtered by: ${filterStatus}`)
             }
           </p>
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
             {/* Search Box */}
             <div className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm border border-gray-200">
               <Search className="w-4 h-4 text-gray-500 ml-2" />
               <input
                 type="text"
                 placeholder="Search by name or phone"
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 className="text-sm border-none focus:ring-0 text-gray-800 w-full outline-none"
               />
               {searchTerm && (
                 <button 
                   onClick={() => setSearchTerm('')}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   ×
                 </button>
               )}
             </div>

             {/* Status Filter */}
             <div className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm border border-gray-200">
               <Filter className="w-4 h-4 text-gray-500 ml-2" />
               <span className="text-sm font-medium text-gray-600">Status:</span>
               <select 
                 value={filterStatus} 
                 onChange={(e) => setFilterStatus(e.target.value as any)}
                 className="form-select text-sm border-none focus:ring-0 text-gray-800 font-semibold cursor-pointer bg-transparent outline-none"
               >
                 <option value="Active">Active (Default)</option>
                 <option value="All">All Statuses</option>
                 <option disabled>──────────</option>
                 {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
               </select>
            </div>

            {/* Location Filter */}
            <div className="flex items-center space-x-2 bg-white p-2 rounded-lg shadow-sm border border-gray-200">
               <span className="text-sm font-medium text-gray-600 ml-2">Location:</span>
               <select 
                 value={filterLocation} 
                 onChange={(e) => setFilterLocation(e.target.value as any)}
                 className="form-select text-sm border-none focus:ring-0 text-gray-800 font-semibold cursor-pointer bg-transparent outline-none"
               >
                 <option value="All">All Locations</option>
                 {LOCATIONS.map(loc => <option key={loc} value={loc}>{loc}</option>)}
               </select>
            </div>
        </div>
      </div>

      {dates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <Calendar className="w-12 h-12 text-gray-300 mb-4" />
              <p className="text-gray-500 font-medium">No active orders found for this filter.</p>
          </div>
      )}

      {dates.map(date => {
          const { dayName, fullDate } = formatDateHeader(date);
          const dateOrders = groupedOrders[date];

          return (
            <div key={date} className="space-y-4">
                <div className="flex items-baseline space-x-3 border-b border-gray-200 pb-2">
                    <h3 className="text-xl font-bold text-red-900">{dayName}</h3>
                    <span className="text-gray-500 font-medium">{fullDate}</span>
                    <span className="ml-auto bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full">{dateOrders.length} Orders</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {dateOrders.map(order => (
                        <div key={order.id} className="h-full">
                            <OrderCard 
                                order={order} 
                                onUpdateStatus={onUpdateStatus}
                                onEdit={onEditOrder}
                                onItemToggle={onItemToggle}
                            />
                        </div>
                    ))}
                </div>
            </div>
          );
      })}
    </div>
  );
};

export default Dashboard;
